import React from 'react';
import { AlertTriangle, LogOut, CreditCard } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';

/**
 * Page displayed when a student's account is financially suspended.
 * Shows a message explaining the suspension and instructions to resolve it.
 *
 * This is different from SystemSuspendedPage which is for institution-level suspension.
 * FinancialSuspendedPage is for individual student account suspension due to unpaid fees.
 */
export const FinancialSuspendedPage: React.FC = () => {
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    // Clear financial suspension flag from localStorage
    localStorage.removeItem('nexedu_financial_suspended');
    logout();
    window.location.href = '/login';
  };

  // Extract suspension message from localStorage if available
  const suspensionMessage = localStorage.getItem('nexedu_financial_suspension_message') || '';

  // Try to extract amount from the message
  const amountMatch = suspensionMessage.match(/Montant impaye:\s*([\d\s]+)\s*MAD/i);
  const unpaidAmount = amountMatch ? amountMatch[1].trim() : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-8 text-center bg-gradient-to-r from-red-500 to-orange-500">
            <div className="h-16 w-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} className="text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">
              Compte Suspendu
            </h1>
            <p className="text-white/90 text-sm mt-2">
              Suspension pour raison financiere
            </p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <div className="text-center">
              <p className="text-gray-600 leading-relaxed">
                Bonjour <span className="font-semibold">{user?.firstName || 'Etudiant'}</span>,
              </p>
              <p className="text-gray-600 leading-relaxed mt-2">
                Votre acces a la plateforme a ete suspendu en raison de frais de scolarite impayes.
              </p>
            </div>

            {/* Unpaid Amount Card */}
            {unpaidAmount && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-red-700 mb-2">
                  <CreditCard size={20} />
                  <span className="font-semibold">Montant impaye</span>
                </div>
                <p className="text-2xl font-bold text-red-600">
                  {unpaidAmount} MAD
                </p>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="font-semibold text-amber-800 mb-2">
                Pour reactiver votre acces:
              </h3>
              <ul className="text-sm text-amber-700 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">1.</span>
                  <span>Rendez-vous au service financier de votre etablissement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">2.</span>
                  <span>Regularisez votre situation (paiement total ou partiel selon accord)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">3.</span>
                  <span>Votre acces sera reactive immediatement apres validation du paiement</span>
                </li>
              </ul>
            </div>

            {/* Notice */}
            <div className="text-center text-sm text-gray-500">
              <p>
                Si vous pensez qu'il s'agit d'une erreur ou si vous avez deja effectue le paiement,
                veuillez contacter le service financier avec votre preuve de paiement.
              </p>
            </div>

            {/* Logout Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <LogOut size={18} />
                Se deconnecter
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          {user?.tenantName || 'NEXEDU'} - Plateforme de Gestion Educative
        </p>
      </div>
    </div>
  );
};
